import React from "react";
import { History, LocationListener, createBrowserHistory } from "history";
import { setActivePageSet } from "src/utils/redux/slices/linearBuyflow";
import getStore from "src/utils/redux/store";
import { getPage, getPageSet } from "../pageSets";
import { Router } from "react-router";
import { captureException } from "src/utils/error";

const history = createPageSetAwareHistory();

export function getHistory() {
  return history;
}

// Implements custom history, allowing for us to manage
// LBF state prior to any rerendering.
export function PageSetRouter({ children }) {
  return <Router history={history}>{children}</Router>;
}

/**
 * Factory to create customized history for page sets.
 *
 * This little screaming bundle of joy wraps the core browser history instance
 * with a customized listener lifecycle. This allows us to defer render updates
 * from back+forward navigation until the page set has been properly
 * initialized.
 *
 * This wrapper is needed as:
 * 1. The router binds itself as the first listener, meaning that by the time
 *   we get to our listeners passed to their APIs, the router has already
 *   rendered the page, leading to inconsistent pageset state, which will
 *   likely be fatal.
 *
 * 2. The navigation management APIs in the native history API implementation do not
 *   allow for async blocking of request or any sort of chaining of listeners.
 *   Since pagesets need async to init, we need to take a more active role in
 *   the onpopstate lifecycle.s
 */
function createPageSetAwareHistory(): History<unknown> {
  const browserHistory = createBrowserHistory<{ pageSetId: string }>();

  const downstreamListeners = new Set<LocationListener>();

  function listen(listener: LocationListener) {
    downstreamListeners.add(listener);
    return () => {
      downstreamListeners.delete(listener);
    };
  }

  let eventSequence = 0;
  browserHistory.listen(async (newLocation, action) => {
    eventSequence += 1;
    const currentEvent = eventSequence;

    // Only need to reload page set on back/forward navigation.
    if (action === "POP") {
      const store = getStore();
      const state = store.getState();
      const statePageSet = getPageSet(newLocation.state?.pageSetId);
      const statePage = getPage(
        statePageSet?.pages,
        newLocation.pathname,
        state
      );

      // If the state for the new location defines a pageset different than
      // the current, then pause the (react) navigation until we're loaded.
      if (statePage && statePageSet.id !== state.linearBuyflow.pageSetName) {
        await store.dispatch(setActivePageSet(statePageSet));

        // Delay here should be small as the page set should have already
        // been primed by this point. There is a slight chance that a race
        // occurs here if there are multiple back to back navigation
        // events. When this occurs, avoid sending out of date events to
        // the router.
        if (eventSequence !== currentEvent) {
          if (statePageSet.id !== browserHistory.location.state.pageSetId) {
            // This could be an expected error, if there is still a concurrent
            // set call in flight. Logging for now and we can examine prevalence
            // after.
            captureException(
              new Error(
                `Navigation occurred with pending pageset. Set pageset: ${statePageSet.id} Active pageset: ${browserHistory.location.state.pageSetId}`
              )
            );
          }
          return;
        }
      }
    }

    // Propagate to react router, etc.
    downstreamListeners.forEach((listener) => listener(newLocation, action));
  });

  // Lets over overwrite only the listen function, while still
  // retaining any field mutations that history may have.
  return new Proxy(browserHistory, {
    get(target, prop) {
      if (prop === "listen") {
        return listen;
      }
      if (prop === "type") {
        return "pageSet";
      }
      return target[prop];
    },
  });
}
